import React, { useEffect, useId, useRef, useState } from 'react'

import { ChartView } from './ChartView'
import { formatNumber } from './chartFormat'

/**
 * The bank lookup chart: any FDIC-insured bank open today, or one that failed
 * since 1985, with its deposits or assets each quarter since 1984. The page's
 * own data (`chart.data`) is the featured bank's deposits; everything else
 * comes from GET /banks/:cert.json (BanksController). Many banks share a name
 * ("Citizens Bank"), so matches are listed with their city and picked by the
 * FDIC's certificate number. `?bank=628&measure=assets` opens a bank directly,
 * and the address follows each lookup so a result can be shared.
 */

const FEATURED = { cert: 628, name: 'JPMorgan Chase Bank', city: 'Columbus', state: 'OH', failed: null } // keep in step with chart.yml
const MEASURES = {
  deposits: { button: 'Deposits', yLabel: 'Deposits, dollars', title: (name) => `Deposits at ${name} each quarter` },
  assets: { button: 'Assets', yLabel: 'Assets, dollars', title: (name) => `What ${name} owned (its assets) each quarter` },
}

async function getJson(path, signal) {
  const response = await fetch(path, { headers: { Accept: 'application/json' }, signal })
  if (response.status === 404) return null
  if (!response.ok) throw new Error(`Request failed (${response.status})`)
  return response.json()
}

function formatDate(iso) {
  const date = new Date(`${iso}T00:00:00`)
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

const exactDollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

function place(bank) {
  return [bank.city, bank.state].filter(Boolean).join(', ')
}

function setAddress(cert, measure) {
  const url = new URL(window.location.href)
  url.searchParams.set('bank', cert)
  url.searchParams.set('measure', measure)
  window.history.replaceState(null, '', url)
}

export function BankLookup({ chart }) {
  const inputId = useId()
  const [result, setResult] = useState({ ...FEATURED, measures: { deposits: chart.data } })
  const [measure, setMeasure] = useState('deposits')
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [status, setStatus] = useState({ loading: false, message: null })
  const lookupRef = useRef(null)

  const lookUp = async (raw, preferredMeasure = measure) => {
    const id = String(raw).trim()
    if (!id) return
    lookupRef.current?.abort()
    const controller = new AbortController()
    lookupRef.current = controller
    setStatus({ loading: true, message: null })
    try {
      const found = await getJson(`/banks/${encodeURIComponent(id)}.json`, controller.signal)
      if (!found) {
        setStatus({ loading: false, message: `No bank found for “${id}”. Try part of its name, such as Chase or Wells Fargo.` })
        return
      }
      const chosen = MEASURES[preferredMeasure] ? preferredMeasure : 'deposits'
      setResult(found)
      setMeasure(chosen)
      setSuggestions([])
      setStatus({ loading: false, message: null })
      setAddress(found.cert, chosen)
    } catch (error) {
      if (error.name === 'AbortError') return
      setStatus({ loading: false, message: 'That bank couldn’t be loaded from the FDIC. Please try again.' })
    }
  }

  // Open the bank in the address, if any.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('bank')) lookUp(params.get('bank'), params.get('measure'))
    return () => lookupRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // List banks whose name starts with what has been typed.
  useEffect(() => {
    const prefix = query.trim()
    if (prefix.length < 2) {
      setSuggestions([])
      return undefined
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      getJson(`/banks.json?q=${encodeURIComponent(prefix)}`, controller.signal)
        .then((body) => setSuggestions(body?.banks ?? []))
        .catch(() => {})
    }, 200)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query])

  const chooseMeasure = (value) => {
    if (!result.measures[value]) {
      lookUp(result.cert, value) // the featured bank arrives with its deposits only
      return
    }
    setMeasure(value)
    setAddress(result.cert, value)
  }

  const meta = MEASURES[measure]
  const points = result.measures[measure] ?? []
  const latest = points[points.length - 1]

  return (
    <div className="w-full">
      <form
        className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(event) => { event.preventDefault(); lookUp(suggestions[0]?.cert ?? query) }}
      >
        <div className="min-w-0 flex-1">
          <label htmlFor={inputId} className="form-label">Find a bank</label>
          <input
            id={inputId}
            type="search"
            className="input-form-field"
            placeholder="A bank's name, e.g. Wells Fargo or Silicon Valley"
            autoComplete="off"
            value={query}
            maxLength={40}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <button type="submit" className="btn-primary min-h-10" disabled={status.loading}>
          {status.loading ? 'Loading…' : 'Show'}
        </button>
      </form>

      {suggestions.length ? (
        <ul className="mb-4 divide-y divide-line rounded-lg border border-line" aria-label="Matching banks">
          {suggestions.map((bank) => (
            <li key={bank.cert}>
              <button
                type="button"
                className="flex min-h-10 w-full flex-col items-start px-3 py-2 text-left hover:bg-surface-muted sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                onClick={() => { setQuery(''); lookUp(bank.cert) }}
              >
                <span className="font-medium text-ink break-words">{bank.name}</span>
                <span className="text-sm text-ink-muted">
                  {place(bank)}{bank.failed ? `, failed ${formatDate(bank.failed)}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {status.message ? <p className="alert-info mb-4" role="status">{status.message}</p> : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="font-semibold text-ink break-words">
          {result.name}
          <span className="block text-sm font-normal text-ink-muted">
            {place(result)}{result.failed ? `. Failed ${formatDate(result.failed)}` : ''}
            {latest ? `. ${latest.label}: ${formatNumber(latest.value, 'dollars')}.` : ''}
          </span>
        </p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Measure">
          {Object.entries(MEASURES).map(([value, { button }]) => (
            <button
              key={value}
              type="button"
              className={`${measure === value ? 'btn-primary' : 'btn-secondary'} btn-sm min-h-10`}
              aria-pressed={measure === value}
              disabled={status.loading}
              onClick={() => chooseMeasure(value)}
            >
              {button}
            </button>
          ))}
        </div>
      </div>

      {points.length >= 2 ? (
        <ChartView
          searchable={false}
          chart={{ ...chart, title: meta.title(result.name), y_label: meta.yLabel, data: points }}
        />
      ) : (
        <p className="alert-info" role="status">
          The FDIC has {points.length ? 'only one quarter' : 'no quarters'} of {meta.button.toLowerCase()} for {result.name}.
        </p>
      )}

      {points.length ? (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold text-primary">The numbers for {result.name}</summary>
          <div className="mt-2 max-h-80 overflow-y-auto">
            <table className="admin-table w-full">
              <thead>
                <tr>
                  <th scope="col" className="text-left">Quarter</th>
                  <th scope="col" className="text-right">{meta.button}</th>
                </tr>
              </thead>
              <tbody>
                {[...points].reverse().map((p) => (
                  <tr key={p.label}>
                    <td>{p.label}</td>
                    <td className="text-right tabular-nums">{exactDollars.format(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </div>
  )
}
